import React, { useState, ChangeEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
  Radio,
  Grid,
  Colors,
  Button,
  Typography,
  Loader,
  Tooltip,
  AnalyticsComponent,
} from '@inplayer-org/inplayer-ui';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import { RiUploadLine } from 'react-icons/ri';
import { MdInfoOutline } from 'react-icons/md';

// common
import { downloadErrorFile } from 'ducks/merchantDucks/common/api';
import { API } from 'constants/api';

// utils
import { isDevelopmentEnv } from 'utils/helpers';

// actions
import { getTemplateFile, bulkAccessActions, getErrorFiles } from 'ducks/merchantDucks/accesses/actions';

// selectors
import {
  getBulkAccessErrorFiles,
  getIsFetching,
  getIsFetchingErrorFiles,
} from 'ducks/merchantDucks/accesses/selectors';
import { getMerchantId } from 'ducks/merchantDucks/user/selectors';

// components
import ContentHolder from 'components/ContentHolder';
import UploadFile from 'components/UploadFile';
import BulkErrorsTable from 'pages/MerchantPages/Access/BulkErrosTable';

// styled components
import { BlueTypography, StyledButtonSpan, StyledSpan } from 'components/SharedStyledElements';
import { RadioButtonsContainer } from 'pages/MerchantPages/Audience/components/modals/GrantAccessModal';
import { StyledNavLink, Container } from 'pages/MerchantPages/Assets/components/Navbar/AdvancedEmbedding/styles';
import { LoaderContainer } from 'pages/MerchantPages/Assets/Packages/ManageAssets/DroppableColumn';
import AttentionMessage from 'components/AttentionMessage';

const { Cell } = Grid;

const Wrapper = styled.div<{ isAdmin: boolean }>`
  margin: 2rem 0;
  ${({ isAdmin }) =>
    !isAdmin &&
    css`
      background-color: ${Colors.white};
      padding: 2rem;
      border-radius: 5px;
      box-shadow: 0 0 8px 1px ${transparentize(0.87, Colors.fontDarkGray)};
    `}
`;

const StyledDiv = styled.div<{ marginTop?: string }>`
  margin-top: ${({ marginTop }) => marginTop || '1rem'};
`;

const SubmitButton = styled(Button)`
  margin-top: 1.5rem;
  width: 65%;
`;

const StyledIcon = styled(RiUploadLine)`
  vertical-align: middle;
  width: 20px;
  height: 20px;
`;

const StyledCell = styled(Cell)`
  margin-top: 1rem;
`;

const TitleWrapper = styled.div`
  display: flex;
`;
const ActionTitle = styled(Typography)`
  margin: 0 0 1rem 0.4rem;
`;
const StyledTooltip = styled(Tooltip)`
  padding-top: 3px;
`;

const AttentionWrapper = styled.div`
  margin-top: 30px;
`;

enum AccessActionTypes {
  GRANT_ACCESS = 'grant',
  REVOKE_ACCESS = 'revoke',
  EXTEND_ACCESS = 'extend',
}

const BulkAccessActions: React.FC = () => {
  const dispatch = useDispatch();
  const [actionType, setActionType] = useState('');
  const [file, setFile] = useState<File>();
  const { id: adminMerchantId } = useParams<{ id: string }>();

  const errorFiles = useSelector(getBulkAccessErrorFiles);
  const merchantId = useSelector(getMerchantId);
  const isLoading = useSelector(getIsFetching);
  const isFetchingErrorFiles = useSelector(getIsFetchingErrorFiles);

  const isDisabled = !file || actionType === '';

  useEffect(() => {
    if (actionType) {
      dispatch(getErrorFiles(actionType));
    }
  }, [getErrorFiles, actionType]);

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setActionType(e.target.value);
  };

  const handleTemplateFile = () => {
    dispatch(getTemplateFile(actionType));
  };

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }
    await dispatch(bulkAccessActions({ file: file!, actionType, merchantId: adminMerchantId }));
    setFile(undefined);
  };

  const onFileDrop = async (fileForSubmit: File) => {
    setFile(fileForSubmit);
  };

  const downloadFile = async (fileName: string) => {
    const endpoint = API.DOWNLOAD_FAILED_FILE;
    const action = actionType;
    const id = adminMerchantId || merchantId;
    await downloadErrorFile({ endpoint, fileName, merchantId: Number(id), action });
  };

  return (
    <>
      {!adminMerchantId && (
        <Container>
          <AnalyticsComponent>
            {({ pages, tracker, merchantId: mId, ip }) => (
              <StyledNavLink
                onClick={() => {
                  tracker.track({
                    event: 'click',
                    type: 'link',
                    tag: 'link_back_access',
                    pages,
                    merchantId: mId,
                    ip,
                  });
                }}
                to="/access"
              >
                &larr; back to Access
              </StyledNavLink>
            )}
          </AnalyticsComponent>
        </Container>
      )}
      <ContentHolder>
        {!adminMerchantId && <BlueTypography variant="h2">Bulk actions</BlueTypography>}
        <AttentionWrapper>{isDevelopmentEnv && <AttentionMessage />}</AttentionWrapper>
        <Wrapper isAdmin={!!adminMerchantId}>
          <RadioButtonsContainer columns={5} margin="1rem 0 0 0">
            <StyledCell width={1}>
              <TitleWrapper>
                <StyledTooltip placement="top" content="Provided dates must be in UTC">
                  <MdInfoOutline name="info" />
                </StyledTooltip>
                <ActionTitle variant="h5">Choose action: </ActionTitle>
              </TitleWrapper>

              <Radio
                id={AccessActionTypes.GRANT_ACCESS}
                value={AccessActionTypes.GRANT_ACCESS}
                name={AccessActionTypes.GRANT_ACCESS}
                label="Grant access"
                onChange={handleRadioChange}
                checked={actionType === AccessActionTypes.GRANT_ACCESS}
              />
              <Radio
                id={AccessActionTypes.REVOKE_ACCESS}
                value={AccessActionTypes.REVOKE_ACCESS}
                name={AccessActionTypes.REVOKE_ACCESS}
                label="Revoke access"
                onChange={handleRadioChange}
                checked={actionType === AccessActionTypes.REVOKE_ACCESS}
              />
              <Radio
                id={AccessActionTypes.EXTEND_ACCESS}
                value={AccessActionTypes.EXTEND_ACCESS}
                name={AccessActionTypes.EXTEND_ACCESS}
                label="Extend access"
                onChange={handleRadioChange}
                checked={actionType === AccessActionTypes.EXTEND_ACCESS}
              />

              <SubmitButton tag="button_submit" disabled={isDisabled} onClick={handleSubmit}>
                <StyledIcon />
                <StyledButtonSpan>Submit</StyledButtonSpan>
              </SubmitButton>
            </StyledCell>
            <Cell width={4}>
              <StyledDiv>
                {isLoading ? (
                  <LoaderContainer>
                    <Loader />
                  </LoaderContainer>
                ) : (
                  <UploadFile uploadImage={onFileDrop} />
                )}
                {actionType && (
                  <StyledDiv>
                    <AnalyticsComponent>
                      {({ pages, tracker, merchantId: mId, ip }) => (
                        <Typography variant="p">
                          Update the access of multiple customers by uploading a csv file. When uploading, make sure to
                          use{' '}
                          <StyledSpan
                            isColor
                            onClick={() => {
                              tracker.track({
                                event: 'click',
                                type: 'link',
                                tag: 'link_this_template',
                                pages,
                                merchantId: mId,
                                ip,
                              });
                              handleTemplateFile();
                            }}
                          >
                            this template.
                          </StyledSpan>
                        </Typography>
                      )}
                    </AnalyticsComponent>
                  </StyledDiv>
                )}
              </StyledDiv>
            </Cell>
          </RadioButtonsContainer>
          {errorFiles && errorFiles.length > 0 && (
            <StyledDiv marginTop="2rem">
              <BlueTypography variant="h6">Error files</BlueTypography>
              <StyledDiv marginTop="1rem">
                <BulkErrorsTable
                  isFetchingErrorFiles={isFetchingErrorFiles}
                  collection={errorFiles}
                  downloadFile={downloadFile}
                  isAdmin={!!adminMerchantId}
                />
              </StyledDiv>
            </StyledDiv>
          )}
        </Wrapper>
      </ContentHolder>
    </>
  );
};

export default BulkAccessActions;
